import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import './WelcomeBanner.css';

const STORAGE_KEY = 'revtrack_welcome_dismissed';

const DEFAULT_TRAINING_URL = 'https://getrevtrack.com/training';
const CUTOFF_DATE = new Date('2026-03-30T00:00:00Z');

const WelcomeBanner = ({ trainingUrl = DEFAULT_TRAINING_URL, userCreatedAt }) => {
    const { user, isLoaded } = useUser();
    const [visible, setVisible] = useState(false);
    const syncedDismiss = useRef(false);

    // Dismissal is stored on the Clerk user so it follows the account to every device.
    // localStorage only avoids a flash on this browser before Clerk responds.
    useEffect(() => {
        if (!isLoaded || !userCreatedAt) return;

        const dismissedInClerk = user?.unsafeMetadata?.[STORAGE_KEY] === true;
        const dismissedLocally = localStorage.getItem(STORAGE_KEY) === '1';

        if (dismissedInClerk) {
            localStorage.setItem(STORAGE_KEY, '1');
        } else if (dismissedLocally && user && !syncedDismiss.current) {
            syncedDismiss.current = true;
            user.update({
                unsafeMetadata: { ...user.unsafeMetadata, [STORAGE_KEY]: true },
            }).catch(() => {
                syncedDismiss.current = false;
            });
        }

        try {
            const isNewUser = new Date(userCreatedAt) > CUTOFF_DATE;
            setVisible(isNewUser && !dismissedInClerk && !dismissedLocally);
        } catch {
            setVisible(false);
        }
    }, [isLoaded, userCreatedAt, user]);

    useEffect(() => {
        if (visible) {
            document.body.classList.add('welcome-banner-active');
        } else {
            document.body.classList.remove('welcome-banner-active');
        }
        return () => document.body.classList.remove('welcome-banner-active');
    }, [visible]);

    if (!visible) return null;

    const handleDismiss = () => {
        localStorage.setItem(STORAGE_KEY, '1');
        setVisible(false);
        if (!user) return;
        user.update({
            unsafeMetadata: { ...user.unsafeMetadata, [STORAGE_KEY]: true },
        }).catch(() => {});
    };

    return (
        <div className="welcome-banner" role="banner">
            <button
                className="welcome-banner-close"
                onClick={handleDismiss}
                aria-label="Dismiss welcome banner"
            >
                ✕
            </button>
            <div className="welcome-banner-content">
                <span className="welcome-banner-emoji" aria-hidden="true">🎉</span>
                <p className="welcome-banner-text">
                    <strong>Welcome to Revtrack!</strong> Watch our short training to learn how to use Revtrack.
                </p>
                <a
                    href={trainingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="welcome-banner-cta"
                >
                    🎥 Watch Training →
                </a>
            </div>
        </div>
    );
};

export default WelcomeBanner;
